import type {
  Block,
  CommonBlocksCompoundIndexes,
  ComponentBlock,
} from '@basehub/data/replicache/index'
import { TreeManager } from '@basehub/utils/tree/index'
import { ListView } from '@basehub/data/replicache/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { BaseHubCollectionColumn } from './types'
import { invariant } from '@epic-web/invariant'

export const filterColumnMetadata = (
  cm: ListView['columnsMetadata'][number],
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes>
): { shouldShow: boolean; column?: Block | '__title' } => {
  if (cm.blockId === '__title') {
    // never hide title column
    // if (cm.hidden) return { shouldShow: false, column: '__title' }
    return { shouldShow: true, column: '__title' }
  }
  // filter out stale columns in columnsMetadata, of blocks that were deleted
  const block = treeManager.getNode(cm.blockId)?.value
  if (!block) return { shouldShow: false }
  if (cm.hidden) return { shouldShow: false, column: block }
  if (block.type === 'comment') return { shouldShow: false }

  return { shouldShow: true, column: block }
}

export const resolveVisibleColumns = ({
  selectedView,
  listTemplateId,
  treeManager,
}: {
  selectedView: ListView | undefined
  listTemplateId: ComponentBlock['id']
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
}) => {
  if (!treeManager) return []
  if (!treeManager.getNode(listTemplateId)) return []
  const columnsMetadata = selectedView?.columnsMetadata ?? []
  let hasTitleColumn = false
  const touched = new Set<string>()
  const listTemplateChildren =
    treeManager
      .getChildrenKeys(listTemplateId)
      .map((key) => treeManager.getNode(key, { required: true }).value) ?? []
  const columns: BaseHubCollectionColumn[] = []

  if (selectedView) {
    columnsMetadata.forEach((cm) => {
      if (cm.blockId === '__title') hasTitleColumn = true
      const result = filterColumnMetadata(cm, treeManager)
      touched.add(cm.blockId)
      if (!result.shouldShow || !result.column) return
      const { column } = result

      if (column === '__title') {
        columns.push({
          icon: 'letter-capitalize',
          title: 'Title',
          type: 'text' as const,
          id: '__title',
          width: cm.width,
        })
      } else {
        let alias = ''
        if (column.type === 'media') {
          const allowedTypes = Object.keys(
            column.constraints?.allowedMimeTypes ?? {}
          )
          if (allowedTypes.length === 1 && allowedTypes[0]) {
            alias = allowedTypes[0]
          }
        }
        columns.push({
          icon: `${column.type}${alias ? `+${alias}` : ''}`,
          title: column.title || 'Untitled',
          type: column.type,
          id: idToSourceBlockId(column),
          width: cm.width,
        })
      }
    })
    listTemplateChildren.forEach((c) => {
      const childSourceBlockId = idToSourceBlockId(c)
      if (touched.has(childSourceBlockId)) return
      if (c.type === 'comment') return
      const result = filterColumnMetadata(
        {
          blockId: childSourceBlockId,
          width: 200,
          hidden: false,
        },
        treeManager
      )
      if (!result.shouldShow || !result.column) return

      let alias = ''
      if (c.type === 'media') {
        const allowedTypes = Object.keys(c.constraints?.allowedMimeTypes ?? {})
        if (allowedTypes.length === 1 && allowedTypes[0]) {
          alias = allowedTypes[0]
        }
      }

      columns.push({
        icon: `${c.type}${alias ? `+${alias}` : ''}`,
        title: c.title || 'Untitled',
        type: c.type,
        id: childSourceBlockId,
        width: 200,
      })
    })
  } else if (!selectedView) {
    listTemplateChildren.forEach((c) => {
      if (c.type === 'comment') return
      const childSourceBlockId = idToSourceBlockId(c)
      const isAlreadyInColumns = columns.find(
        (col) => col.id === childSourceBlockId
      )
      if (isAlreadyInColumns) return

      columns.push({
        icon: c.type,
        title: c.title || 'Untitled',
        type: c.type,
        id: childSourceBlockId,
        width: 200,
      })
    })
  }

  if (!hasTitleColumn || columns.length === 0) {
    columns.unshift({
      icon: 'letter-capitalize',
      title: 'Title',
      type: 'text' as const,
      id: '__title',
      width: 200,
    })
  }

  return columns
}

export const resolveColumnsMetadata = ({
  selectedView,
  listTemplateId,
  treeManager,
}: {
  selectedView: ListView | undefined
  listTemplateId: ComponentBlock['id']
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
}) => {
  if (!treeManager) return []

  const listTemplate = treeManager.getNode(listTemplateId, {
    required: true,
  }).value
  invariant(
    listTemplate.type === 'component',
    'Expected list template to be a component'
  )

  const templateChildren = Boolean(listTemplate.id && treeManager)
    ? treeManager?.getChildrenKeys(
        listTemplate.sourceBlockId ?? listTemplate.id
      )
    : []

  let options: ListView['columnsMetadata'] = []
  const touched = new Set<string>()
  let titleWasAdded = false

  ;(selectedView?.columnsMetadata ?? []).forEach((cm) => {
    const result = filterColumnMetadata(cm, treeManager)
    const { column } = result

    if (column === '__title') {
      const titleOption = {
        blockId: '__title',
        hidden: false,
        width: cm.width,
      }
      options.push(titleOption)
      touched.add('__title')
      titleWasAdded = true
      return
    }

    if (!column) return

    const option: (typeof options)[number] = {
      blockId: idToSourceBlockId(column),
      hidden: cm.hidden,
      width: cm.width,
    }
    touched.add(column.id)
    options.push(option)
  })
  if (!titleWasAdded || !selectedView) {
    options = [
      {
        blockId: '__title',
        width: 200,
        hidden: false,
      },
      ...options,
    ]
  }

  templateChildren.forEach((childId) => {
    if (touched.has(childId)) return
    const result = filterColumnMetadata(
      {
        blockId: childId,
        width: 200,
        hidden: false,
      },
      treeManager
    )

    if (!result.shouldShow || !result.column) return
    const { column } = result
    if (column === '__title') return
    const option: (typeof options)[number] = {
      blockId: idToSourceBlockId(column),
      width: 200,
      hidden: false,
    }

    options.push(option)
  })

  return options
}
